// Comments Section Component for ArticleScreen
import React, {useState, useEffect, useCallback} from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  Image,
} from 'react-native';
import {API_CONFIG} from '../services/config';
import {useAuth} from '../hooks/useAuth';
import {useTheme} from '../hooks/useTheme';
import {getStoredToken} from '../services/auth';
import {Icon} from './Icon';
import {AdBanner} from './AdBanner';
import {useCommentAds} from './article/ArticleContentWithAds';

interface Comment {
  id: number;
  name: string;
  content: string;
  avatar_url: string;
  created_at: string;
  is_admin: boolean;
  is_pending?: boolean;
  is_own?: boolean;
  likes_count?: number;
  dislikes_count?: number;
  user_vote?: 'like' | 'dislike' | null;
  replies?: Comment[];
}

interface CommentsProps {
  slug: string;
}

export const Comments: React.FC<CommentsProps> = ({slug}) => {
  const {isAuthenticated} = useAuth();
  const {colors} = useTheme();
  const commentAds = useCommentAds();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState('');
  
  // State for reply functionality
  const [replyingTo, setReplyingTo] = useState<number | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [postingReply, setPostingReply] = useState(false);
  
  // Track user votes (optimistic UI)
  const [userVotes, setUserVotes] = useState<Record<number, 'like' | 'dislike' | null>>({});

  const fetchComments = useCallback(async () => {
    try {
      setLoading(true);
      const token = await getStoredToken();
      const headers: Record<string, string> = {};
      if (token) {
        headers.Authorization = `Bearer ${token}`;
      }
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/app/posts/${slug}/comments`,
        {headers}
      );
      
      if (!response.ok) {
        console.log('Comments API not available yet');
        setComments([]);
        return;
      }
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        console.log('Comments API returned non-JSON response');
        setComments([]);
        return;
      }
      
      const data = await response.json();
      if (data.success) {
        const fetchedComments = data.comments || [];
        setComments(fetchedComments);
        // Initialize userVotes from server response
        const votes: Record<number, 'like' | 'dislike' | null> = {};
        fetchedComments.forEach((c: Comment) => {
          if (c.user_vote) votes[c.id] = c.user_vote;
          c.replies?.forEach((r: Comment) => {
            if (r.user_vote) votes[r.id] = r.user_vote;
          });
        });
        setUserVotes(votes);
      }
    } catch {
      console.log('Comments not available');
      setComments([]);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    fetchComments();
  }, [fetchComments]);

  const handleSubmit = async () => {
    if (!newComment.trim() || !isAuthenticated) return;

    setPosting(true);
    setError('');

    try {
      const token = await getStoredToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/app/posts/${slug}/comments`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({content: newComment}),
        }
      );

      const data = await response.json();

      if (data.success) {
        setComments([
          {
            ...data.comment,
            is_pending: true,
            is_own: true,
            likes_count: 0,
            dislikes_count: 0,
            replies: [],
          },
          ...comments,
        ]);
        setNewComment('');
      } else {
        setError(data.message || 'Errore nell\'invio');
      }
    } catch {
      setError('Errore di connessione');
    } finally {
      setPosting(false);
    }
  };

  // Handle like action
  const handleLike = async (commentId: number) => {
    if (!isAuthenticated) return;
    
    // Optimistic update
    const previousVote = userVotes[commentId];
    setUserVotes(prev => ({...prev, [commentId]: previousVote === 'like' ? null : 'like'}));
    
    // Update local count
    setComments(prev => prev.map(c => {
      if (c.id === commentId) {
        const likeDelta = previousVote === 'like' ? -1 : 1;
        const dislikeDelta = previousVote === 'dislike' ? -1 : 0;
        return {
          ...c,
          likes_count: (c.likes_count || 0) + likeDelta,
          dislikes_count: (c.dislikes_count || 0) + dislikeDelta,
        };
      }
      return c;
    }));

    try {
      const token = await getStoredToken();
      await fetch(`${API_CONFIG.BASE_URL}/api/app/comments/${commentId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    } catch {
      // Revert on error
      setUserVotes(prev => ({...prev, [commentId]: previousVote}));
    }
  };

  // Handle dislike action
  const handleDislike = async (commentId: number) => {
    if (!isAuthenticated) return;
    
    const previousVote = userVotes[commentId];
    setUserVotes(prev => ({...prev, [commentId]: previousVote === 'dislike' ? null : 'dislike'}));
    
    setComments(prev => prev.map(c => {
      if (c.id === commentId) {
        const dislikeDelta = previousVote === 'dislike' ? -1 : 1;
        const likeDelta = previousVote === 'like' ? -1 : 0;
        return {
          ...c,
          likes_count: (c.likes_count || 0) + likeDelta,
          dislikes_count: (c.dislikes_count || 0) + dislikeDelta,
        };
      }
      return c;
    }));

    try {
      const token = await getStoredToken();
      await fetch(`${API_CONFIG.BASE_URL}/api/app/comments/${commentId}/dislike`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
    } catch {
      setUserVotes(prev => ({...prev, [commentId]: previousVote}));
    }
  };

  // Handle reply submission
  const handleReplySubmit = async (parentId: number) => {
    if (!replyContent.trim() || !isAuthenticated) return;

    setPostingReply(true);

    try {
      const token = await getStoredToken();
      const response = await fetch(
        `${API_CONFIG.BASE_URL}/api/app/comments/${parentId}/reply`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({content: replyContent}),
        }
      );

      const data = await response.json();

      if (data.success) {
        // Add reply to parent comment
        setComments(prev => prev.map(c => {
          if (c.id === parentId) {
            return {
              ...c,
              replies: [...(c.replies || []), data.comment],
            };
          }
          return c;
        }));
        setReplyContent('');
        setReplyingTo(null);
      }
    } catch {
      console.log('Reply failed');
    } finally {
      setPostingReply(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getInitials = (name: string): string => {
    if (!name) return '??';
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  const renderAvatar = (avatarUrl: string | null | undefined, name: string, size: 'normal' | 'small' = 'normal') => {
    const isGeneratedAvatar = avatarUrl && (
      avatarUrl.includes('ui-avatars.com') || 
      avatarUrl.includes('default') ||
      avatarUrl.trim() === ''
    );
    const hasValidAvatar = avatarUrl && !isGeneratedAvatar;
    
    const avatarStyle = size === 'small' ? styles.replyAvatar : styles.avatar;
    const initialsCircleStyle = size === 'small' ? styles.replyInitialsCircle : styles.initialsCircle;
    const initialsTextStyle = size === 'small' ? styles.replyInitialsText : styles.initialsText;

    if (hasValidAvatar) {
      return <Image source={{uri: avatarUrl}} style={avatarStyle} />;
    }

    return (
      <View style={[initialsCircleStyle, {backgroundColor: colors.primary}]}>
        <Text style={initialsTextStyle}>{getInitials(name)}</Text>
      </View>
    );
  };

  // Dynamic styles based on theme
  const dynamicStyles = {
    container: {backgroundColor: colors.background},
    sectionTitle: {color: colors.textPrimary},
    input: {
      backgroundColor: colors.surface,
      color: colors.textPrimary,
      borderColor: colors.border,
    },
    error: {color: colors.error},
    submitButton: {backgroundColor: colors.primary},
    loginPrompt: {backgroundColor: colors.surfaceVariant},
    loginPromptText: {color: colors.textSecondary},
    emptyText: {color: colors.textSecondary},
    commentItem: {borderBottomColor: colors.divider},
    authorName: {color: colors.textPrimary},
    adminName: {color: colors.primary},
    adminBadge: {color: colors.primary},
    commentDate: {color: colors.textSecondary},
    commentText: {color: colors.textPrimary},
    repliesContainer: {borderLeftColor: colors.primaryPastel},
    replyAuthor: {color: colors.textPrimary},
    replyText: {color: colors.textPrimary},
    actionText: {color: colors.textSecondary},
    activeVote: {color: colors.primary},
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="small" color={colors.primary} />
      </View>
    );
  }

  return (
    <View style={[styles.container, dynamicStyles.container]}>
      <Text style={[styles.sectionTitle, dynamicStyles.sectionTitle]}>
        Commenti ({comments.length})
      </Text>

      {/* Comment input */}
      {isAuthenticated ? (
        <View style={styles.inputContainer}>
          <TextInput
            style={[styles.input, dynamicStyles.input]}
            placeholder="Scrivi un commento..."
            placeholderTextColor={colors.textSecondary}
            value={newComment}
            onChangeText={setNewComment}
            multiline
            maxLength={2000}
          />
          {error ? <Text style={[styles.error, dynamicStyles.error]}>{error}</Text> : null}
          <TouchableOpacity
            style={[styles.submitButton, dynamicStyles.submitButton, posting && styles.submitButtonDisabled]}
            onPress={handleSubmit}
            disabled={posting || !newComment.trim()}>
            {posting ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              <Text style={styles.submitButtonText}>INVIA</Text>
            )}
          </TouchableOpacity>
        </View>
      ) : (
        <View style={[styles.loginPrompt, dynamicStyles.loginPrompt]}>
          <Text style={[styles.loginPromptText, dynamicStyles.loginPromptText]}>
            Accedi per lasciare un commento
          </Text>
        </View>
      )}

      {/* Ad after comments form */}
      {commentAds.afterForm && (
        <AdBanner
          position="middle"
          imageUrl={commentAds.afterForm.image_url || undefined}
          targetUrl={commentAds.afterForm.target_url || undefined}
        />
      )}

      {/* Comments list */}
      {comments.length === 0 ? (
        <View style={styles.emptyState}>
          <Text style={[styles.emptyText, dynamicStyles.emptyText]}>Nessun commento ancora.</Text>
          <Text style={[styles.emptySubtext, dynamicStyles.emptyText]}>Sii il primo a commentare!</Text>
        </View>
      ) : (
        <View style={styles.commentsList}>
          {comments.map((item, index) => (
            <React.Fragment key={item.id}>
              <View style={[styles.commentItem, dynamicStyles.commentItem]}>
                {renderAvatar(item.avatar_url, item.name, 'normal')}
                <View style={styles.commentContent}>
                  <View style={styles.commentHeader}>
                    <Text style={[styles.authorName, dynamicStyles.authorName, item.is_admin && dynamicStyles.adminName]}>
                      {item.name}
                      {item.is_admin && <Text style={dynamicStyles.adminBadge}> ✓</Text>}
                    </Text>
                    <Text style={[styles.commentDate, dynamicStyles.commentDate]}>{formatDate(item.created_at)}</Text>
                  </View>
                  {item.is_pending && (
                    <View style={styles.pendingBadge}>
                      <Icon name="clock-rotate-left" size={11} color="#b8860b" />
                      <Text style={styles.pendingBadgeText}>In attesa di approvazione</Text>
                    </View>
                  )}
                  <Text style={[styles.commentText, dynamicStyles.commentText, item.is_pending && {opacity: 0.7}]}>{item.content}</Text>
                  
                  {/* Action buttons row */}
                  <View style={styles.actionsRow}>
                    {/* Like button */}
                    <TouchableOpacity 
                      style={styles.actionButton} 
                      onPress={() => handleLike(item.id)}
                      disabled={!isAuthenticated}
                    >
                      <Icon 
                        name="thumbs-up" 
                        size={14} 
                        color={userVotes[item.id] === 'like' ? colors.primary : colors.textSecondary}
                      />
                      <Text style={[
                        styles.actionCount, 
                        dynamicStyles.actionText,
                        userVotes[item.id] === 'like' && dynamicStyles.activeVote
                      ]}>
                        {item.likes_count || 0}
                      </Text>
                    </TouchableOpacity>

                    {/* Dislike button */}
                    <TouchableOpacity 
                      style={styles.actionButton} 
                      onPress={() => handleDislike(item.id)}
                      disabled={!isAuthenticated}
                    >
                      <Icon 
                        name="thumbs-down" 
                        size={14} 
                        color={userVotes[item.id] === 'dislike' ? colors.error : colors.textSecondary}
                      />
                      <Text style={[
                        styles.actionCount, 
                        dynamicStyles.actionText,
                        userVotes[item.id] === 'dislike' && {color: colors.error}
                      ]}>
                        {item.dislikes_count || 0}
                      </Text>
                    </TouchableOpacity>

                    {/* Reply button */}
                    {isAuthenticated && (
                      <TouchableOpacity 
                        style={styles.replyButton} 
                        onPress={() => setReplyingTo(replyingTo === item.id ? null : item.id)}
                      >
                        <Icon name="reply" size={14} color={colors.primary} />
                        <Text style={[styles.replyButtonText, {color: colors.primary}]}>Rispondi</Text>
                      </TouchableOpacity>
                    )}
                  </View>

                  {/* Reply input (expandable) */}
                  {replyingTo === item.id && (
                    <View style={styles.replyInputContainer}>
                      <TextInput
                        style={[styles.replyInput, dynamicStyles.input]}
                        placeholder="Scrivi una risposta..."
                        placeholderTextColor={colors.textSecondary}
                        value={replyContent}
                        onChangeText={setReplyContent}
                        multiline
                        maxLength={1000}
                      />
                      <View style={styles.replyActions}>
                        <TouchableOpacity 
                          style={styles.cancelButton}
                          onPress={() => {
                            setReplyingTo(null);
                            setReplyContent('');
                          }}
                        >
                          <Text style={[styles.cancelButtonText, {color: colors.textSecondary}]}>Annulla</Text>
                        </TouchableOpacity>
                        <TouchableOpacity 
                          style={[styles.sendReplyButton, {backgroundColor: colors.primary}]}
                          onPress={() => handleReplySubmit(item.id)}
                          disabled={postingReply || !replyContent.trim()}
                        >
                          {postingReply ? (
                            <ActivityIndicator size="small" color="#fff" />
                          ) : (
                            <Text style={styles.sendReplyText}>Invia</Text>
                          )}
                        </TouchableOpacity>
                      </View>
                    </View>
                  )}
                  
                  {/* Replies */}
                  {item.replies && item.replies.length > 0 && (
                    <View style={[styles.repliesContainer, dynamicStyles.repliesContainer]}>
                      {item.replies.map((reply) => (
                        <View key={reply.id} style={styles.replyItem}>
                          {renderAvatar(reply.avatar_url, reply.name, 'small')}
                          <View style={styles.replyContent}>
                            <Text style={[styles.replyAuthor, dynamicStyles.replyAuthor]}>{reply.name}</Text>
                            {reply.is_pending && (
                              <View style={styles.pendingBadgeSmall}>
                                <Icon name="clock-rotate-left" size={9} color="#b8860b" />
                                <Text style={styles.pendingBadgeTextSmall}>In attesa</Text>
                              </View>
                            )}
                            <Text style={[styles.replyText, dynamicStyles.replyText, reply.is_pending && {opacity: 0.7}]}>{reply.content}</Text>
                          </View>
                        </View>
                      ))}
                    </View>
                  )}
                </View>
              </View>
              
              {/* Ad after 3rd comment */}
              {index === 2 && commentAds.afterThirdComment && (
                <AdBanner
                  position="middle"
                  imageUrl={commentAds.afterThirdComment.image_url || undefined}
                  targetUrl={commentAds.afterThirdComment.target_url || undefined}
                />
              )}
            </React.Fragment>
          ))}
          
          {/* Ad after all comments */}
          {commentAds.afterAllComments && (
            <AdBanner
              position="middle"
              imageUrl={commentAds.afterAllComments.image_url || undefined}
              targetUrl={commentAds.afterAllComments.target_url || undefined}
            />
          )}
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 16,
  },
  loadingContainer: {
    padding: 32,
    alignItems: 'center',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 16,
  },
  inputContainer: {
    marginBottom: 20,
  },
  input: {
    borderRadius: 8,
    padding: 12,
    fontSize: 14,
    minHeight: 80,
    textAlignVertical: 'top',
    borderWidth: 1,
  },
  error: {
    fontSize: 12,
    marginTop: 8,
  },
  submitButton: {
    borderRadius: 20,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 10,
  },
  submitButtonDisabled: {
    opacity: 0.6,
  },
  submitButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  loginPrompt: {
    padding: 16,
    borderRadius: 8,
    marginBottom: 16,
    alignItems: 'center',
  },
  loginPromptText: {
    fontSize: 14,
  },
  emptyState: {
    padding: 24,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
  },
  emptySubtext: {
    fontSize: 12,
    marginTop: 4,
  },
  commentsList: {
    marginTop: 8,
  },
  commentItem: {
    flexDirection: 'row',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  commentContent: {
    flex: 1,
  },
  commentHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 4,
  },
  authorName: {
    fontSize: 14,
    fontWeight: '600',
  },
  commentDate: {
    fontSize: 11,
  },
  commentText: {
    fontSize: 14,
    lineHeight: 20,
  },
  // Action buttons
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 10,
    gap: 16,
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  actionCount: {
    fontSize: 12,
    fontWeight: '500',
  },
  replyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginLeft: 'auto',
  },
  replyButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },
  // Reply input
  replyInputContainer: {
    marginTop: 12,
  },
  replyInput: {
    borderRadius: 8,
    padding: 10,
    fontSize: 13,
    minHeight: 60,
    textAlignVertical: 'top',
    borderWidth: 1,
  },
  replyActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 8,
  },
  cancelButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
  },
  cancelButtonText: {
    fontSize: 13,
  },
  sendReplyButton: {
    paddingVertical: 6,
    paddingHorizontal: 16,
    borderRadius: 16,
  },
  sendReplyText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  // Replies
  repliesContainer: {
    marginTop: 12,
    marginLeft: 8,
    paddingLeft: 12,
    borderLeftWidth: 2,
  },
  replyItem: {
    flexDirection: 'row',
    marginBottom: 10,
  },
  replyAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    marginRight: 10,
  },
  replyContent: {
    flex: 1,
  },
  replyAuthor: {
    fontSize: 12,
    fontWeight: '600',
    marginBottom: 2,
  },
  replyText: {
    fontSize: 13,
    lineHeight: 18,
  },
  // Initials circle for users without avatar
  initialsCircle: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
  initialsText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '700',
  },
  replyInitialsCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    marginRight: 10,
    alignItems: 'center',
    justifyContent: 'center',
  },
  replyInitialsText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: '700',
  },
  pendingBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    backgroundColor: '#fdf6e3',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 10,
    alignSelf: 'flex-start',
    marginBottom: 4,
  },
  pendingBadgeText: {
    fontSize: 11,
    color: '#b8860b',
    fontWeight: '600',
  },
  pendingBadgeSmall: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    backgroundColor: '#fdf6e3',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
    alignSelf: 'flex-start',
    marginBottom: 3,
  },
  pendingBadgeTextSmall: {
    fontSize: 9,
    color: '#b8860b',
    fontWeight: '600',
  },
});
